import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Loader2 } from "lucide-react";

const hasAuthParams = (search) => {
  const params = new URLSearchParams(search);
  return params.has("code") || params.has("state") || params.has("error");
};

// Detects Auth0's "email not verified" access_denied error text
const isEmailNotVerifiedError = (text) => {
  if (!text) return false;
  const lower = text.toLowerCase();
  return (
    lower.includes("verify your email") ||
    lower.includes("verification link") ||
    lower.includes("email is not verified")
  );
};

export default function AuthCallbackSpinner() {
  const { isLoading } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();

  // Capture the error_description from the URL while Auth0 is still loading.
  // The SDK may clean the URL before isLoading flips to false, so we grab it early.
  const pendingEmailError = useRef(null);

  useEffect(() => {
    if (!isLoading) return;
    const params = new URLSearchParams(location.search);
    if (
      params.get("error") === "access_denied" &&
      isEmailNotVerifiedError(params.get("error_description") || "")
    ) {
      pendingEmailError.current = params.get("error_description");
    }
  }, [isLoading, location.search]);

  // Once Auth0 finishes processing, redirect to /verify-email if we captured an email
  // error from the URL. This is intentionally one-shot: the ref is cleared after redirect
  // so navigating back to "/" never re-triggers the redirect.
  useEffect(() => {
    if (isLoading || !pendingEmailError.current) return;

    const message = pendingEmailError.current;
    pendingEmailError.current = null;
    navigate(
      `/verify-email?message=${encodeURIComponent(message)}`,
      { replace: true }
    );
  }, [isLoading, navigate]);

  if (!isLoading || !hasAuthParams(location.search)) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/80 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3 text-[#324c48]">
        <Loader2 className="h-8 w-8 animate-spin" />
        <p className="text-sm font-medium">Completing sign in...</p>
      </div>
    </div>
  );
}
